import React, { FunctionComponent, useMemo } from 'react';
import { IPrice } from '../../../../../../../common/model/IPrice';
import SingleCurrencyCash from '../../../presentational/prices/singleCurrency/cash/SingleCurrencyCash';
import { IFinanceQuote } from '../../../../../common/model/IFinanceQuote';
import FinancePriceDisplayMulti from '../../../presentational/prices/multiCurrency/finance/FinancePriceDisplayMulti';
import SingleCurrency from '../../../presentational/prices/singleCurrency/finance/SingleCurrency';
import MultiCurrencyCashContainer from './multiCurrency/cash/MultiCurrencyCashContainer';
import useFinancePackageFrequencyLabelProvider from '../../../../../common/react/useFinancePackageFrequencyLabelProvider';
import { IPriceDisplay } from '../../../../../common/model/IPlatformConfig';

export interface IFinanceProductSection {
  disabled?: boolean;
  loading?: boolean;
  isCash: boolean;
  hasTradeIn: boolean;
  isMultiCurrency: boolean;
  hasComparisonRate: boolean;
  primaryPrice: IPrice;
  financeQuote?: Partial<IFinanceQuote>;
  secondaryCurrencyQuote: Partial<IFinanceQuote>;
  shouldDisplayFinance: boolean;
  priceDisplay: IPriceDisplay;
  onFinanceCalculate: () => void;
  onDriveawayIndicativeClick: () => void;
}

const PriceSection: FunctionComponent<IFinanceProductSection> = ({
  disabled,
  loading,
  isCash,
  hasTradeIn,
  isMultiCurrency,
  primaryPrice,
  financeQuote,
  secondaryCurrencyQuote,
  shouldDisplayFinance,
  onFinanceCalculate,
  onDriveawayIndicativeClick,
  hasComparisonRate,
  priceDisplay,
}) => {
  const financePackageFrequencyLabelProvider = useFinancePackageFrequencyLabelProvider();
  const isCashProduct = useMemo(() => !financeQuote || financeQuote.isCashProduct, [financeQuote]);

  if (isMultiCurrency && isCashProduct) {
    return <MultiCurrencyCashContainer onIndicativeClick={onDriveawayIndicativeClick} />;
  }

  if (isMultiCurrency && !isCashProduct) {
    return (
      <FinancePriceDisplayMulti
        primaryFinanceQuote={financeQuote}
        secondaryCurrencyQuote={secondaryCurrencyQuote}
        onIndicativeClick={onDriveawayIndicativeClick}
        isDisabled={disabled}
        onFinanceCalculate={onFinanceCalculate}
        priceDisplay={priceDisplay}
      />
    );
  }

  if (!isMultiCurrency && isCashProduct) {
    return (
      <SingleCurrencyCash
        loading={loading}
        hasTradeIn={hasTradeIn}
        primaryPrice={primaryPrice}
        isCash={isCash}
        onIndicativeClick={onDriveawayIndicativeClick}
      />
    );
  }

  return (
    <SingleCurrency
      disabled={disabled}
      loading={loading}
      paymentValue={{
        basePrice: financeQuote?.monthlyPayment ?? 0,
        currency: financeQuote?.currency?.code,
      }}
      paymentFrequency={financePackageFrequencyLabelProvider(financeQuote.packageCode, 'long')}
      onFinanceCalculate={onFinanceCalculate}
      shouldDisplayFinance={shouldDisplayFinance}
      hasComparisonRate={hasComparisonRate}
      decimalScale={priceDisplay.finance?.decimalScale ?? 2}
    />
  );
};

export default PriceSection;
